// src/components/Users/Forms/Login.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import { login, googleAuth, reset, resendVerification } from '../../../redux/slices/Users/authSlice';
import { Helmet } from 'react-helmet-async';

const Login = () => {
  const { t, i18n } = useTranslation('common');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const { email, password } = formData;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, isLoading, isError, message, pendingVerificationEmail } = useSelector((state) => state.auth);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/profile');
    }
    dispatch(reset());
  }, [user, navigate, dispatch]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setResent(false);
    dispatch(login({ email, password }));
  };

  const handleResend = async () => {
    try {
      await resendVerification({
        email: pendingVerificationEmail,
        lang: i18n.language?.startsWith('en') ? 'en' : 'es',
      });
    } catch {
      // Rate limited or offline - nothing useful to add for the user.
    }
    setResent(true);
  };

  const handleGoogleSuccess = (credentialResponse) => {
    dispatch(googleAuth(credentialResponse.credential));
  };

  const handleGoogleError = () => {
    console.error('Google sign-in failed');
  };

  return (
    <>
      <Helmet>
        <title>{t('meta.login.title')}</title>
        <meta name="description" content={t('meta.login.description')} />
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-100 to-red-200 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-2xl shadow-xl">
          <div>
            {/* Dog image - already 15% larger (h-28 w-28) */}
            <div className="flex justify-center mb-4">
              <img
                src="https://res.cloudinary.com/dc6xthiol/image/upload/v1743023819/loginImg_es917n.jpg"
                alt={t('login.imageAlt')}
                className="h-32 w-32 rounded-full object-cover shadow-lg"
              />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              {t('login.title')}
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {t('login.subtitle')}
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {isError && pendingVerificationEmail && (
              <div className="bg-yellow-50 border border-yellow-400 text-yellow-800 px-4 py-3 rounded-lg relative space-y-2">
                <p>{t('login.notVerified')}</p>
                {resent ? (
                  <p className="text-sm font-medium">{t('login.resent')}</p>
                ) : (
                  <button
                    type="button"
                    onClick={handleResend}
                    className="text-sm font-medium text-red-600 hover:text-red-500 underline"
                  >
                    {t('login.resendButton')}
                  </button>
                )}
              </div>
            )}
            {isError && !pendingVerificationEmail && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg relative">
                {message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('login.emailLabel')}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={handleChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                  placeholder={t('login.emailPlaceholder')}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('login.passwordLabel')}
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={handleChange}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                  placeholder={t('login.passwordPlaceholder')}
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                  isLoading
                    ? 'bg-red-400 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                }`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-t-2 border-white rounded-full animate-spin"></div>
                ) : (
                  t('login.submitButton')
                )}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">{t('login.dividerText')}</span>
            </div>
          </div>

          {/* Google Sign-in Button */}
          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text="signin_with"
              shape="rectangular"
            />
          </div>

          <div className="text-sm text-center">
            <span className="text-gray-600">{t('login.noAccount')} </span>
            <Link to="/register" className="font-medium text-red-600 hover:text-red-500">
              {t('login.registerLink')}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;